import React, { useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";

import { APP_NAME } from "../constants";
import { useUser } from "../context/UserContext";
import { useAlertContext } from "../context/AlertContext";
import corporateAccountService from "../services/corporateAccountService";
import LoadingSpinner from "./LoadingSpinner";

const issueCategories = [
  { id: 1, name: "App Bug" },
  { id: 2, name: "Billing Issue" },
  { id: 3, name: "Incorrect Data" },
  { id: 4, name: "Host Location Problem" },
  { id: 5, name: "Other" },
];

const CorporateReportIssue = () => {
  const { user, userCorporateAccount } = useUser();
  const { setAlert } = useAlertContext();

  const [category, setCategory] = useState(issueCategories[0].name);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // ✅ NEW

  const resetForm = () => {
    setCategory(issueCategories[0].name);
    setDescription("");
    setSubmitted(false);
  };

  const validateForm = (): boolean => {
    if (!user?.id) {
      setAlert("error", "User not found. Please log in again.");
      return false;
    }
    if (!userCorporateAccount?.id) {
      setAlert("error", "Corporate account not found.");
      return false;
    }
    if (!category) {
      setAlert("error", "Please select a category.");
      return false;
    }
    if (!description.trim()) {
      setAlert("error", "Please enter a description.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await corporateAccountService.saveCorporateIssueReport(
        category,
        description,
        user!.id,
        userCorporateAccount!.id
      );

      if (result) {
        setSubmitted(true); // ✅ hide form
        setAlert("success", "Your issue has been reported.");
      } else {
        setAlert("error", "Unable to report issue. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setAlert("error", "Unable to report issue. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  return (
    <MDBox
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        p: 3,
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      }}
    >
      {loading && <LoadingSpinner title="Submitting your issue..." />}

      <MDTypography variant="h4" fontWeight="bold" mb={3}>
        Report an Issue
      </MDTypography>

      {/* ✅ SUCCESS STATE */}
      {submitted ? (
        <MDBox textAlign="center" py={4}>
          <MDTypography variant="h5" fontWeight="bold" mb={2}>
            Your issue has been submitted
          </MDTypography>

          <MDTypography variant="body2" color="text">
            Our team will review it and follow up if needed.
            <br />
            Thank you for being a {APP_NAME} partner!
          </MDTypography>

          <MDButton
            sx={{ mt: 4 }}
            variant="gradient"
            color="info"
            onClick={resetForm}
          >
            Submit Another Issue
          </MDButton>
        </MDBox>
      ) : (
        <>
          {/* Category */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="issue-category-label">Category</InputLabel>
            <Select
              labelId="issue-category-label"
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
              sx={{
                height: 50,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "50px",
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}
            >
              {issueCategories.map((item) => (
                <MenuItem
                  key={item.id}
                  value={item.name}
                  sx={{ minHeight: 48 }}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Description */}
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Please Describe the Issue"
            placeholder="Describe the issue..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 3 }}
          />

          <MDTypography variant="body2" color="text" mb={4}>
            We apologize for this inconvenience and will contact you as soon as
            possible to resolve this issue.
            <br />
            Thank you for being a {APP_NAME} partner!
          </MDTypography>

          {/* Actions */}
          <MDBox display="flex" justifyContent="flex-end">
            <MDBox display="flex" gap={2}>

              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </MDButton>
            <MDButton
                variant="gradient"
                color="info"
                onClick={handleSubmit}
              >
                Submit Issue
              </MDButton>

            </MDBox>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default CorporateReportIssue;
